import React, { useState, useMemo, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { Formatter } from '../format';
import { Placeholder } from './Placeholder';
import { SentimentIcon } from './SentimentIcon';
import { TrendIcon } from './TrendIcon';
import { useLocalStorage, useSessionStorage } from '../common/localStorage';

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Default table preferences
export const DEFAULT_PREFERENCES = {
  pageSize: 10,
  sortField: 'timestamp',
  sortDirection: 'desc',
  visibleColumns: [],
  wrapLines: false,
  stripedRows: false
};

// Column definitions for the contact table
const BASE_COLUMNS = [
  {
    id: "timestamp",
    header: "Timestamp",
    sortingField: "timestamp",
    cell: (d) => {
      if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
        return Formatter.Timestamp(d.timestamp);
      } else {
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/dashboard/${d.key || d.filename}`;
            }}
            className="text-cyan-600 hover:text-cyan-800 hover:underline transition-colors"
          >
            {Formatter.Timestamp(d.timestamp)}
          </button>
        );
      }
    },
    minWidth: 160
  },
  {
    id: "jobName",
    header: "Job Name",
    cell: (d) => {
      if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
        return d.jobName;
      } else {
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/dashboard/${d.key || d.filename}`;
            }}
            className="text-cyan-600 hover:text-cyan-800 hover:underline font-medium transition-colors"
          >
            {d.guid} - {d.timestamp}
          </button>
        );
      }
    },
    isRowHeader: true,
    sortingField: "jobName",
  },
  {
    id: "assessed",
    header: "Assessed",
    cell: (d) => {
      console.log("Assessed cell - jobName:", d.jobName, "assessed value:", d.assessed, "type:", typeof d.assessed);
      if (d.assessed === true) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ✓ Assessed
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Not Assessed
        </span>
      );
    },
    sortingField: "assessed",
    minWidth: 130,
  },
  {
    id: "guid",
    header: "GUID",
    cell: (d) => (
      <span className="font-mono text-sm">{d.guid}</span>
    ),
    isRowHeader: true,
    sortingField: "guid",
    minWidth: 150
  },
  {
    id: "agent",
    header: "Agent",
    cell: (d) => d.agent,
    isRowHeader: true,
    sortingField: "agent",
    minWidth: 130
  },
  {
    id: "customer",
    header: "Campaign",
    cell: (d) => d.customer,
    isRowHeader: true,
    sortingField: "customer",
    minWidth: 130
  },
  // {
  //   id: "queue",
  //   header: "Queue",
  //   cell: (d) => d.queue,
  //   isRowHeader: true,
  //   sortingField: "queue",
  //   minWidth: 130
  // },
  {
    id: "callerSentimentScore",
    header: "Cust Sent",
    sortingField: "callerSentimentScore",
    cell: (d) => (
      <div className="flex items-center justify-center space-x-2">
        <SentimentIcon score={d?.callerSentimentScore} size="sm" />
        <TrendIcon trend={d.callerSentimentChange} size="sm" />
        <span className="text-sm">{Math.floor(d.callerSentimentScore) || '-'}</span>
      </div>
    ),
    minWidth: 130
  },
  {
    id: "langCode",
    header: "Lang Code",
    cell: (d) => d.lang,
    minWidth: 100,
  },
  {
    id: "duration",
    header: "Duration",
    cell: (d) => Formatter.Time(d.duration),
    minWidth: 130,
  }
];

const getMatchesCountText = (count) => {
  return count === 1 ? `1 match` : `${count} matches`;
};

const Loading = (columns) =>
  columns.map((c, i) => (
    <td key={i} className="px-6 py-4">
      <Placeholder />
    </td>
  ));

const NoMatches = ({ children, colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="px-6 py-8 text-center text-gray-500">
      {children || (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p>Try adjusting your search criteria or filters.</p>
        </div>
      )}
    </td>
  </tr>
);

export const ContactTable = ({
  email,
  group,
  data = [],
  loading = false,
  empty,
  header,
  variant = 'embedded',
  onRowClick,
  assignments = {},
  users = [],
  onAssign,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false
}) => {
  const history = useHistory();
  
  const [preferences, setPreferences] = useLocalStorage(
    'contact-table-preferences',
    DEFAULT_PREFERENCES,
  );
  console.log("DATA : ", data)
  const [callKey , setCallKey] =useState('')
  const [callResult, setCallResult] = useState('')
  const [sortField, setSortField] = useState(preferences.sortField);
  const [sortDirection, setSortDirection] = useState(preferences.sortDirection);
  const [currentPage, setCurrentPage] = useSessionStorage('contact-table-current-page', 1);
  const [pageSize, setPageSize] = useState(preferences.pageSize);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Handle row click
  const handleRowClick = (item) => {
    if (onRowClick) {
      onRowClick(item);
    } else {
      history.push(`/dashboard/${item.key || item.filename}`);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);
    
    // Save preferences
    setPreferences({
      ...preferences,
      sortField: field,
      sortDirection: newDirection
    });
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filteredData = [...data];

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply column filters
    Object.entries(filters).forEach(([field, filterValue]) => {
      if (filterValue) {
        filteredData = filteredData.filter(item =>
          String(item[field]).toLowerCase().includes(String(filterValue).toLowerCase())
        );
      }
    });

    // Apply sorting
    if (sortField) {
      filteredData.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        // Handle timestamps
        if (sortField === 'timestamp') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        // Primary sort comparison
        let primaryComparison = 0;

        // Handle numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          primaryComparison = sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        } else {
          // Handle strings
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();

          if (sortDirection === 'asc') {
            primaryComparison = aVal.localeCompare(bVal);
          } else {
            primaryComparison = bVal.localeCompare(aVal);
          }
        }

        // If primary values are equal, sort by timestamp as secondary
        if (primaryComparison === 0 && sortField !== 'timestamp') {
          const aTime = new Date(a.timestamp).getTime();
          const bTime = new Date(b.timestamp).getTime();
          // Always sort timestamp descending (newest first) for secondary sort
          return bTime - aTime;
        }

        return primaryComparison;
      });
    }

    return filteredData;
  }, [data, searchTerm, filters, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(processedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = processedData.slice(startIndex, startIndex + pageSize);

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    setPreferences({
      ...preferences,
      pageSize: newPageSize
    });
  };
  // useEffect(() => {
  //     setCallKey(data[0].key)
  //     setCallResult(data[0].Result)
  // }, [])
  // console.log("Call Key: ", callKey)
  // console.log("Call Res: ", callResult)

  const [open, setOpen] = useState(false);
  const handleOpenModal = (e) => {
      setCallKey(data[0].key)
      setCallResult(data[0].Result)
    e.stopPropagation(); // Prevent row click propagation if applicable
    setOpen(true);
  };

  const EditResultModal = ({open, callResult, callKey, email, onClose}) => {
    console.log("MAIL : ", email)
    let key = callKey
    const [compliance, setCompliance] = useState(callResult?.Compliance ?? true);
    const [score, setScore] = useState(callResult?.QA_Score ?? 0);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      setSubmitting(true);
      const payload = {
        compliance,
        score,
        reason,
        email,
        key,
      };

      try {
        await fetch('/your-api-endpoint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        onClose();
      } catch (err) {
        console.error('Error submitting result update', err);
      } finally {
        setSubmitting(false);
      }
    };

    if (!open) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
          <h2 className="text-lg font-bold mb-4">Edit Result</h2>

          <label className="block mb-2">
            <span className="text-gray-700">Compliance</span>
            <select
              value={compliance ? 'true' : 'false'}
              onChange={(e) => setCompliance(e.target.value === 'true')}
              className="mt-1 block w-full border-gray-300 rounded"
            >
              <option value="true">Compliant</option>
              <option value="false">Non-compliant</option>
            </select>
          </label>

          <label className="block mb-2">
            <span className="text-gray-700">QA Score</span>
            <input
              type="number"
              value={score}
              onChange={(e) => setScore(Number(e.target.value))}
              className="mt-1 block w-full border rounded border-gray-300"
              min={0}
              max={100}
            />
          </label>

          <label className="block mb-4">
            <span className="text-gray-700">Reason for Change</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 block w-full border rounded border-gray-300"
              rows={3}
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };


  const columns = useMemo(() => {
    let cols = [...BASE_COLUMNS];
    // if (group) {
    //   if (group[0] == 'QAAgent') {
    //     const assignCols = [
    //       {
    //         id: 'override',
    //         header: 'Override',
    //         cell: () => {

    //           return (
    //             <>
    //               <button onClick={handleOpenModal} className="px-4 py-2 bg-blue-600 text-white rounded">
    //                 Override
    //               </button>
    //             </>
    //           );
    //         },
    //         sortingField: 'override',
    //         minWidth: 130,
    //       }
    //     ];
    //     cols.splice(2, 0, ...assignCols);
    //   }      
    // }

    return cols;
  }, [users, assignments, onAssign]);

  if (loading) {

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {header && (
          <div className="px-6 py-4 border-b border-gray-200">
            {header}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th key={column.id} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  {Loading(columns)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {open && (
        <EditResultModal
          open={open}
          onClose={() => setOpen(false)}
          callKey={callKey}
          callResult={callResult}
          email={email}
        />
      )}
      {/* Header */}
      {header && (
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {header}
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {getMatchesCountText(processedData.length)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search all columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field w-full pl-10"
            />
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort(column.id)}
                  style={{ minWidth: column.minWidth }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {sortField === column.id && (
                      <span className="text-rewardsco-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length === 0 ? (
              <NoMatches colSpan={columns.length}>{empty}</NoMatches>
            ) : (
              paginatedData.map((item, index) => (
                <tr
                  key={item.key || item.guid || item.id || index}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(item)}
                >
                  {columns.map((column) => (
                    <td key={column.id} className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 align-middle">
                      {column.cell ? column.cell(item) : item[column.id] || '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginatedData.length > 0 && (
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(startIndex + pageSize, processedData.length)} of {processedData.length} results
            </span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="select-field w-28 text-sm"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous Page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 text-sm rounded ${
                      currentPage === pageNum
                        ? 'bg-rewardsco-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next Page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? (
              <>
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Loading...
              </>
            ) : (
              'Load More Calls'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Table preferences component
export const ContactTablePreferences = ({ preferences, onPreferencesChange }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Table Preferences</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rows per Page
          </label>
          <select
            value={preferences.pageSize}
            onChange={(e) => onPreferencesChange({
              ...preferences,
              pageSize: Number(e.target.value)
            })}
            className="select-field"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Sort Field
          </label>
          <select
            value={preferences.sortField}
            onChange={(e) => onPreferencesChange({
              ...preferences,
              sortField: e.target.value
            })}
            className="select-field"
          >
            {BASE_COLUMNS.map(column => (
              <option key={column.id} value={column.id}>
                {column.header}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Sort Direction
          </label>
          <select
            value={preferences.sortDirection}
            onChange={(e) => onPreferencesChange({
              ...preferences,
              sortDirection: e.target.value
            })}
            className="select-field"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={preferences.wrapLines}
              onChange={(e) => onPreferencesChange({
                ...preferences,
                wrapLines: e.target.checked
              })}
              className="rounded border-gray-300 text-rewardsco-600 focus:ring-rewardsco-500"
            />
            <span className="ml-2 text-sm text-gray-700">Wrap long text</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={preferences.stripedRows}
              onChange={(e) => onPreferencesChange({
                ...preferences,
                stripedRows: e.target.checked
              })}
              className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            />
            <span className="ml-2 text-sm text-gray-700">Striped rows</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ContactTable;